import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import TestIntro from './components/TestIntro';
import HollandTest from './components/HollandTest';
import ValueTest from './components/ValueTest';
import TestResult from './components/TestResult';
import TestResumeBanner from '@/components/feature/TestResumeBanner';
import { TEST_PROGRESS_KEYS, readProgress, writeProgress, clearProgress } from '@/lib/testProgress';
import { hollandQuestions } from '@/mocks/hollandQuestions';
import { valueQuestions } from '@/mocks/valueQuestions';

export type TestStep = 'intro' | 'holland' | 'value' | 'result';

export interface TestAnswers {
  holland: Record<string, number>;
  value: Record<string, number>;
}

interface CareerPageProgress {
  step: TestStep;
  holland: Record<string, number>;
  value: Record<string, number>;
}

interface ResumeInfo {
  step: TestStep;
  detail: string;
  holland: Record<string, number>;
  value: Record<string, number>;
}

export default function CareerTestPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState<TestStep>('intro');
  const [answers, setAnswers] = useState<TestAnswers>({ holland: {}, value: {} });
  const [resume, setResume] = useState<ResumeInfo | null>(null);

  // 마운트 시 저장된 진행 상황 확인
  useEffect(() => {
    const page = readProgress<CareerPageProgress>(TEST_PROGRESS_KEYS.careerPage);
    if (!page) return;

    if (page.step === 'value') {
      const v = readProgress<{ current: number; answers: Record<string, number> }>(
        TEST_PROGRESS_KEYS.careerValue,
      );
      const done = v ? Math.min(v.current + 1, valueQuestions.length) : 1;
      setResume({
        step: 'value',
        detail: `직업 가치관 검사 ${done} / ${valueQuestions.length}문항 진행 중 (흥미 검사 완료)`,
        holland: page.holland || {},
        value: page.value || {},
      });
    } else if (page.step === 'holland') {
      const h = readProgress<{ current: number; answers: Record<string, number> }>(
        TEST_PROGRESS_KEYS.careerHolland,
      );
      if (h && (Object.keys(h.answers || {}).length > 0 || h.current > 0)) {
        const done = Math.min(h.current + 1, hollandQuestions.length);
        setResume({
          step: 'holland',
          detail: `흥미 유형 검사 ${done} / ${hollandQuestions.length}문항 진행 중`,
          holland: page.holland || {},
          value: page.value || {},
        });
      }
    }
  }, []);

  // 페이지 상태(단계 + 완료 답안) 자동 저장
  useEffect(() => {
    if (step === 'holland' || step === 'value') {
      writeProgress(TEST_PROGRESS_KEYS.careerPage, {
        step,
        holland: answers.holland,
        value: answers.value,
      });
    }
  }, [step, answers]);

  const clearAll = () => {
    clearProgress(
      TEST_PROGRESS_KEYS.careerPage,
      TEST_PROGRESS_KEYS.careerHolland,
      TEST_PROGRESS_KEYS.careerValue,
    );
    setResume(null);
  };

  const startFresh = () => {
    clearAll();
    setAnswers({ holland: {}, value: {} });
    setStep('holland');
  };

  const resumeTest = () => {
    if (!resume) return;
    setAnswers({ holland: resume.holland || {}, value: resume.value || {} });
    setStep(resume.step);
    setResume(null);
  };

  const handleHollandComplete = (hollandAnswers: Record<string, number>) => {
    clearProgress(TEST_PROGRESS_KEYS.careerHolland);
    setAnswers(prev => ({ ...prev, holland: hollandAnswers }));
    setStep('value');
  };

  const handleValueComplete = (valueAnswers: Record<string, number>) => {
    clearProgress(TEST_PROGRESS_KEYS.careerValue);
    setAnswers(prev => ({ ...prev, value: valueAnswers }));
    setStep('result');
  };

  // 결과 도달 시 진행 상황 정리
  useEffect(() => {
    if (step === 'result') {
      clearProgress(
        TEST_PROGRESS_KEYS.careerPage,
        TEST_PROGRESS_KEYS.careerHolland,
        TEST_PROGRESS_KEYS.careerValue,
      );
      setResume(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  const retake = () => {
    clearAll();
    setAnswers({ holland: {}, value: {} });
    setStep('intro');
  };

  return (
    <div className="min-h-screen bg-background-50">
      {/* Top Nav */}
      <nav className="bg-background-50 border-b border-background-200 sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center gap-3">
          <button
            onClick={() => navigate('/')}
            className="w-8 h-8 flex items-center justify-center text-foreground-600 hover:text-foreground-950 cursor-pointer"
          >
            <i className="ri-arrow-left-line"></i>
          </button>
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 flex items-center justify-center bg-primary-500 rounded-lg">
              <i className="ri-compass-3-line text-white text-xs"></i>
            </div>
            <span className="font-semibold text-foreground-950 text-sm">계열·진로·전공·직업 탐색 검사</span>
          </div>

          {/* Progress */}
          {step !== 'intro' && step !== 'result' && (
            <div className="ml-auto flex items-center gap-2">
              {['holland', 'value'].map((s, idx) => (
                <div
                  key={s}
                  className={`w-7 h-1.5 rounded-full transition-colors ${
                    (step === 'holland' && idx === 0) ||
                    (step === 'value' && idx <= 1)
                      ? 'bg-primary-500'
                      : 'bg-background-200'
                  }`}
                ></div>
              ))}
            </div>
          )}
        </div>
      </nav>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-6 md:py-10">
        {step === 'intro' && (
          <>
            {resume && (
              <div className="max-w-2xl mx-auto mb-6">
                <TestResumeBanner detail={resume.detail} onResume={resumeTest} onReset={clearAll} />
              </div>
            )}
            <TestIntro onStart={startFresh} />
          </>
        )}
        {step === 'holland' && <HollandTest onComplete={handleHollandComplete} />}
        {step === 'value' && <ValueTest onComplete={handleValueComplete} />}
        {step === 'result' && <TestResult answers={answers} onRetake={retake} />}
      </main>
    </div>
  );
}